import { assocPath, clone, filter, map, mergeWith, path } from "ramda";
export function format(first, middle, last) {
    return (first || '') + (middle ? ` ${middle}` : '') + (last ? ` ${last}` : '');
}
export function isBasicType(value) {
    return ["string", "number", "boolean"].includes(typeof value);
}
export function isBasicTypeOrNone(value) {
    return isBasicType(value) || value === null || value === undefined;
}
export function isNullOrUndefined(value) {
    return value === null || value === undefined;
}
export function isArray(type) {
    return Array.isArray(type);
}
export function isObject(type) {
    return typeof type === "object" && type !== null && !isArray(type);
}
export function getPath(pathStr, obj) {
    let attrPath = pathStr.split(".");
    return path(attrPath, obj);
}
export function setPath(obj, pathStr, value) {
    let attrPath = pathStr.split(".");
    return assocPath(attrPath, value, obj);
}
export function isEmptyObject(obj) {
    return Object.keys(obj).length === 0;
}
/**
 * Check if a value is a number or a string that can be converted to a number
 */
export function isNumber(value) {
    return typeof value === "number" || !isNaN(Number(value));
}
export function mergeFormData(a, b, treatEmptyAsNull = false) {
    return mergeWith((a, b) => {
        const isEmpty = b?.toString().trim() === '';
        if (isNullOrUndefined(b) || treatEmptyAsNull && isEmpty) {
            return a;
        }
        return b;
    }, a, b);
}
export function updateCurrentDataWithConversationOutput(currentData, output) {
    if (!output) {
        return currentData;
    }
    let result = clone(currentData);
    if (output && output?.fieldsToDelete && output?.fieldsToDelete.length > 0) {
        for (let field of output.fieldsToDelete) {
            delete result[field];
        }
    }
    // Now we need to merge the result with the output data. This is useful for cases, where user asks to remove all data, but wants to populate some data alongwith in a single step.
    result = mergeFormData(result, output.data);
    return result;
}
export function filterNotNull(values) {
    return values.filter((value) => value !== null && value !== undefined);
}
export function filterNullOrUndefinedDeep(obj) {
    if (isNullOrUndefined(obj)) {
        return;
    }
    if (typeof obj === "object") {
        const result = map(filterNullOrUndefinedDeep, obj);
        //Filter out null and undefined keys
        const filteredResult = filter((value) => value !== null && value !== undefined, result);
        if (Object.keys(filteredResult).length === 0) {
            return;
        }
        return filteredResult;
    }
    if (isArray(obj)) {
        const result = map(filterNullOrUndefinedDeep, obj);
        if (result.length === 0) {
            return;
        }
        //filter out undefined and null
        const filteredResult = result.filter((item) => item !== undefined && item !== null);
        if (filteredResult.length === 0) {
            return;
        }
        return filteredResult;
    }
    if (obj === null || obj === undefined) {
        return;
    }
    return obj;
}
export function isNullOrUndefinedDeep(obj) {
    return filterNullOrUndefinedDeep(obj) === undefined;
}
/**
 * Returns true if the value is "empty" recursively:
 * - null / undefined
 * - empty string (or whitespace-only string)
 * - empty arrays, or arrays whose items are all empty
 * - empty objects, or objects whose values are all empty
 *
 * Notes:
 * - Numbers/booleans are NOT empty (even if 0/false).
 * - Non-plain objects (Date, RegExp, class instances, etc.) are treated as NOT empty.
 * - Cycles are treated as NOT empty to avoid infinite recursion.
 */
export function isEmptyDeep(value, seen = new WeakSet()) {
    if (isNullOrUndefined(value))
        return true;
    if (typeof value === "string")
        return value.trim().length === 0;
    if (isArray(value)) {
        if (value.length === 0)
            return true;
        return value.every((item) => isEmptyDeep(item, seen));
    }
    if (typeof value !== "object")
        return false;
    // Only treat "plain" objects as recursively-empty candidates
    const proto = Object.getPrototypeOf(value);
    const isPlainObject = proto === Object.prototype || proto === null;
    if (!isPlainObject)
        return false;
    if (seen.has(value))
        return false;
    seen.add(value);
    const keys = Object.keys(value);
    if (keys.length === 0)
        return true;
    return keys.every((k) => isEmptyDeep(value[k], seen));
}
export function lowerCaseDeep(obj) {
    if (typeof obj === "string") {
        return obj.toLowerCase();
    }
    if (typeof obj === "object") {
        return map(lowerCaseDeep, obj);
    }
    if (isArray(obj)) {
        return map(lowerCaseDeep, obj);
    }
    return obj;
}
// firstName to First Name
export function camelCaseToNormalCase(str) {
    return str.replace(/([A-Z])/g, " $1").replace(/\b\w/g, char => char.toUpperCase());
}
export function mapKeys(fn, obj) {
    let newObj = {};
    for (const key in obj) {
        newObj[fn(key)] = obj[key];
    }
    return newObj;
}
export function getValueByFilePath(filePath, obj) {
    let pathParts = filePath.split(".");
    return path(pathParts, obj);
}
export function machineFriendly(value) {
    if (!value || typeof value !== 'string') {
        return '';
    }
    // Remove leading/trailing whitespace
    const trimmed = value.trim();
    if (!trimmed) {
        return '';
    }
    // Convert to camelCase
    let result = trimmed
        .toLowerCase()
        .replace(/[^a-z0-9\s\-\_]/g, ' ') // Replace non-alphanumeric (except spaces) with spaces
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .trim() // Remove leading/trailing spaces
        .split(' ') // Split into words
        .map((word, index) => {
        if (index === 0) {
            return word; // First word stays lowercase
        }
        return word.charAt(0).toUpperCase() + word.slice(1); // Capitalize first letter of subsequent words
    })
        .join('');
    // Ensure it starts with a letter (valid variable name)
    if (result && /^[0-9]/.test(result)) {
        result = 'field' + result.charAt(0).toUpperCase() + result.slice(1);
    }
    // Return empty if result is empty or invalid
    return result || '';
}
export function flattenObject(obj, parentKey = "", result = {}, sep = ".") {
    for (const [key, value] of Object.entries(obj)) {
        const newKey = parentKey ? `${parentKey}${sep}${key}` : key;
        if (value && typeof value === "object" && !Array.isArray(value)) {
            flattenObject(value, newKey, result, sep);
        }
        else if (Array.isArray(value)) {
            value.forEach((item, index) => {
                const arrayKey = `${newKey}${sep}${index}`;
                if (item && typeof item === "object") {
                    flattenObject(item, arrayKey, result, sep);
                }
                else {
                    result[arrayKey] = item;
                }
            });
        }
        else {
            result[newKey] = value;
        }
    }
    return result;
}
